// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
/* Starter Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
// import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels"; // Store Custom Code
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
// import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Starter Custom Code
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
// import React, { useContext, useState } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
import React, { useContext, useMemo, useState } from "react";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import PlusCircleFill from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useCanAddToCart, useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import { css } from "styled-components";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { IconWrapper } from "@store/MobiusOverrides/Icon/Icon";

/* Store Custom Code - Start */
import { useIsSignedInAndhasToBeSignedIn } from "@store/Framework/Store/Context/ContextHooks";
import SignInToSeePriceButton from "@store/Library/Components/SignInToSeePriceButton";
import { AddProductApiParameter } from "@insite/client-framework/Services/CartService";
import addLinesToCart from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import {
    CartLineCollectionModel,
    CartLineModel,
    ProductSubscriptionDto,
} from "@insite/client-framework/Types/ApiModels";
import { v4 as uuidv4 } from "uuid";
import getAddToCartForTracking from "@store/Framework/Common/Utilities/getAddToCartForTracking";
/* Store Custom Code - End */

export interface ProductAddToCartButtonProps
    extends Omit<OwnProps, keyof ButtonPresentationProps | keyof LinkPresentationProps> {}

export const useShowProductAddToCartButton = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    const canAddToCart = useCanAddToCart(product, productInfo, configurationCompleted, variantSelectionCompleted);

    /* Store Custom Code - Start */
    const productNotInCatalog = getBooleanProperty(product.properties.notInCatalog);
    /* Store Custom Code - End */

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart || productNotInCatalog); // Store Custom Code
};
/* Starter Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToCartButtonStyles; // Starter Custom Code
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // Starter Custom Code
    overridedStyles?: ProductAddToCartButtonStyles;
    isLink?: boolean;
    showButtonIconOnly?: boolean;
    // Store Custom Code
    selectedAccessories?: ProductInfo[];
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isUpdatingCart, // Starter Custom Code
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    // Store Custom Code
    addLinesToCart,
};

/* Starter Custom Code - Start */
export interface ProductAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    button: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
        `,
    },
    link: linkGetActionLinkProps(PlusCircleFill),
};
/* Starter Custom Code - End */

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    overridedStyles, // Starter Custom Code
    isLink, // Starter Custom Code
    isUpdatingCart, // Starter Custom Code
    showButtonIconOnly, // Starter Custom Code
    /* Store Custom Code - Start */
    selectedAccessories,
    addLinesToCart,
    /* Store Custom Code - End */
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    /* Starter Custom Code - Start */
    const trackAddToCart = ga.use.trackAddToCart();
    const styles = useExpandProps(productAddToCartButtonStyles, extendedStyles, overridedStyles);
    const [isLoading, setIsLoading] = useState(false);

    const buttonStyles = useMemo(() => {
        if (showButtonIconOnly) {
            return mergeProps<ProductAddToCartButtonStyles["button"]>(styles.button, {
                css: css`
                    ${IconWrapper} {
                        margin: initial;
                    }
                `,
            });
        }
        return styles.button;
    }, [showButtonIconOnly, styles.button]);
    /* Starter Custom Code - End */

    /* Store Custom Code - Start */
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn();

    if (!isSignedInAndhasToBeSignedIn) {
        return <SignInToSeePriceButton />;
    }
    /* Store Custom Code - End */

    if (!useShowProductAddToCartButton(configurationCompleted, variantSelectionCompleted) /* Starter Custom Code */) {
        return null;
    }

    /* Store Custom Code - Start */
    const mapProductApiParameterToCartLine = (
        data: AddProductApiParameter,
        qty?: number,
        refId?: string,
        isParent?: boolean,
    ): CartLineModel => {
        const cartLine = {
            productId: data.productId,
            qtyOrdered: qty ?? data.qtyOrdered,
            unitOfMeasure: data.unitOfMeasure,
            notes: data.notes,
            sectionOptions: data.sectionOptions,
            properties: {},
        } as CartLineModel;

        if (data.subscription) {
            cartLine.properties.productSubscription = JSON.stringify(data.subscription);
        }

        if (refId) {
            if (isParent) {
                cartLine.properties.refId = refId;
            } else {
                cartLine.properties.parentRefId = refId;
            }
        }

        return cartLine;
    };
    /* Store Custom Code - End */

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        setIsLoading(true); // Starter Custom Code

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        /* Store Custom Code - Start */
        const addProductParameters: AddProductApiParameter = {
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        };
        /* Store Custom Code - End */

        if (!selectedAccessories?.length /* Store Custom Code */) {
            const cartline = (await addToCart(addProductParameters)) as CartLineModel; // Store Custom Code
            setIsLoading(false); // Starter Custom Code
            trackAddToCart(); // Starter Custom Code

            if (showAddToCartConfirmationDialog) {
                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                    messageType: "success",
                    timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                });
            }
        } else {
            /* Store Custom Code - Start */
            const refId = uuidv4();
            const cartLines = [
                mapProductApiParameterToCartLine(addProductParameters, qtyOrdered, refId, true),
                ...selectedAccessories.map(x => mapProductApiParameterToCartLine(x, qtyOrdered, refId)),
            ];

            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: { cartLines } as CartLineCollectionModel,
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        if (showAddToCartConfirmationDialog) {
                            toasterContext.addToast({
                                body: siteMessage("Cart_AllProductsAddedToCart"),
                                messageType: "success",
                            });
                        }
                        trackAddToCart(getAddToCartForTracking(resultProps.apiResult.cartLines)); // Store Custom Code
                    }
                    setIsLoading(false);
                },
            });
            /* Store Custom Code - End */
        }
    };

    return (
        <>
            {/* Starter Custom Code - Start */}
            {isLink ? (
                <Link
                    {...styles.link}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isUpdatingCart}
                    {...otherProps}
                >
                    {labelOverride ?? translate("Add to Cart")}
                </Link>
            ) : (
                <Button
                    {...buttonStyles}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                    isLoading={isLoading}
                    {...otherProps}
                >
                    {!showButtonIconOnly && (
                        <span /* Store Custom Code */>{labelOverride ?? translate("Add to Cart")}</span>
                    )}
                    {showButtonIconOnly ? (
                        <VisuallyHidden>{labelOverride ?? translate("Add to Cart")}</VisuallyHidden>
                    ) : null}
                </Button>
            )}
            {/* Starter Custom Code - End */}
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
